import React, {type ReactNode} from 'react';
import {useSize} from '../../themes';
import {useFormGroupState} from './hooks';
import {FormHelperTextView} from './FormGroup.styles';

import type {
  SizeContextSize,
  HTMLAttributes,
  SpringTooling,
} from '../__SharedTypes';

export type FormHelperTextProps = {
  /**
   * Content for FormHelperText.
   */
  children?: ReactNode;
  /**
   * The customized color of the helper text - defaults to secondary
   */
  colorVariant?: TextColor;
  /**
   * A manual override for the id of the FormHelperText element.
   */
  id?: string;
  /**
   * The customized size of the helper text, overrides the default size derived from the theme context
   */
  size?: SizeContextSize;
  /**
   * The font style for the helper text.
   */
  font?: 'helpText' | 'brandedHelpText';
} & HTMLAttributes &
  SpringTooling;

/**
 * TextColor options 'succes', 'danger' and 'warning' are covered by FormFeedbackText
 */
type TextColor = 'primary' | 'secondary';

/**
 * # FormHelperText
 *
 * Provides a way to give more context or information for a form input that might be too detailed to include as a placeholder
 *
 * ## Usage
 *
 * ```jsx
 * import {FormGroup, FormFeedbackText} from '@packages/systems/spring';
 *
 * function Example() {
 *   return (
 *     <FormGroup>
 *       <MyCustomInput />
 *       <FormHelperText>{helpText}</FormHelperText>
 *     </FormGroup>
 *   );
 * }
 * ```
 */
export const FormHelperText = React.forwardRef<
  HTMLElement,
  FormHelperTextProps
>(
  (
    {
      children,
      colorVariant = 'secondary',
      font = 'helpText',
      size: sizeProp,
      ...props
    }: FormHelperTextProps,
    ref
  ) => {
    const {helpTextId} = useFormGroupState();
    const size = useSize(sizeProp);

    return (
      <FormHelperTextView
        colorVariant={colorVariant}
        font={font}
        id={helpTextId}
        ref={ref}
        size={size}
        {...props}
        as="p"
      >
        {children}
      </FormHelperTextView>
    );
  }
);

FormHelperText.displayName = 'FormHelperText';
FormHelperText.toString = FormHelperTextView.toString;

export default FormHelperText;
